import {FuncaoMenuV2, GRUPOS_MENU_V2} from './menu-v2.catalogo';
import {DESCRICOES_DAS_FUNCOES, EstadoDoMenuOnboarding, faseDaFuncao, funcaoVisivelNoMenu, QUANDO_CHEGA}
  from '../../../../server/lib/onboarding/FasesDoMenuOnboarding';

/**
 * Menu que cresce com a loja (docs/requisito-onboarding-painel-v1.md, seção 5). Filtro a mais sobre o catálogo,
 * com as fases do arquivo puro do servidor: o que ainda não é da fase vira `somenteBusca` — some da navegação,
 * mas a busca continua achando tudo, e o Descobrir mostra o que está guardado. Nunca libera o que o catálogo não
 * trouxe (módulo, recurso e permissão continuam mandando).
 */
export type OnboardingDoMenu = EstadoDoMenuOnboarding & {experiente?: boolean; novas?: string[]};

export interface GrupoDeGuardadas { nome: string; itens: Array<{funcao: FuncaoMenuV2; descricao: string; quando: string}>; }
/** Quantas funções o menu mostra agora e quantas a loja tem: a placa "Menu simplificado · N de M". */
export interface ContagemDoMenu { visiveis: number; total: number; }

export const FUNCAO_COMECAR: FuncaoMenuV2 = {
  peso: 3, id: 'comecar', rota: '/admin/comecar', nome: 'Primeiros passos', descricao: 'O que falta para o primeiro pedido',
  grupo: 'inicio', secao: 'Primeiros passos', icone: 'fe-flag', termos: 'começar onboarding primeiros passos configurar loja',
  somenteBusca: false
};

/** Ações, conta, itens internos da equipe e o que já é só da busca nunca entram no filtro. */
function foraDoFiltro(f: FuncaoMenuV2): boolean {
  return !!f.acao || f.grupo === 'conta' || !!f.somenteBusca || !!f.interna;
}

export function apliqueFaseDoOnboarding(funcoes: FuncaoMenuV2[], onboarding: OnboardingDoMenu | null | undefined): FuncaoMenuV2[] {
  if (!onboarding) return funcoes;
  const lista = onboarding.ativo && !funcoes.some(f => f.id === FUNCAO_COMECAR.id) ? [FUNCAO_COMECAR, ...funcoes] : funcoes;
  if (onboarding.menuCompleto) return lista;
  return lista.map(f => foraDoFiltro(f) || funcaoVisivelNoMenu(f.id, onboarding) ? f : {...f, somenteBusca: true});
}

/**
 * O que está guardado, agrupado pelos grupos do catálogo (Cardápio, Configurações…), para o dono achar pelo nome
 * que procura; cada função diz quando chega sozinha no menu, se tiver fase.
 */
export function funcoesGuardadas(originais: FuncaoMenuV2[], onboarding: OnboardingDoMenu | null | undefined): GrupoDeGuardadas[] {
  if (!onboarding || onboarding.menuCompleto) return [];
  const guardadas = (originais || []).filter(f => !foraDoFiltro(f) && !funcaoVisivelNoMenu(f.id, onboarding));
  const porGrupo = new Map<string, GrupoDeGuardadas>();
  for (const f of guardadas) {
    const grupo = GRUPOS_MENU_V2.find(g => g.id === f.grupo);
    const nome = grupo?.nome || 'Outras funções';
    if (!porGrupo.has(nome)) porGrupo.set(nome, {nome, itens: []});
    porGrupo.get(nome).itens.push({funcao: f, descricao: DESCRICOES_DAS_FUNCOES[f.id] || f.descricao, quando: quandoChegaSozinha(f.id)});
  }
  const ordem = (nome: string) => { const i = GRUPOS_MENU_V2.findIndex(g => g.nome === nome); return i < 0 ? 99 : i; };
  return Array.from(porGrupo.values()).sort((a, b) => ordem(a.nome) - ordem(b.nome));
}

/** "depois do pedido de teste", "quando chegar o 1º pedido de verdade"… vazio para o que não tem fase. */
export function quandoChegaSozinha(id: string): string {
  const fase = faseDaFuncao(id);
  const texto = fase > 1 ? (QUANDO_CHEGA as any)[fase] as string : '';
  return texto ? texto.charAt(0).toLowerCase() + texto.slice(1) : '';
}

/**
 * Funções navegáveis que o menu mostra agora (a lista já filtrada, com Primeiros passos) e o total que a loja tem
 * (as visíveis mais as guardadas; o catálogo já aplicou plano e permissão).
 */
export function contagemDoMenu(filtradas: FuncaoMenuV2[], guardadas: GrupoDeGuardadas[]): ContagemDoMenu {
  const visiveis = (filtradas || []).filter(f => !foraDoFiltro(f)).length;
  return {visiveis, total: visiveis + (guardadas || []).reduce((n, g) => n + g.itens.length, 0)};
}
